import { domainError, type DomainError } from '../shared/domain-error';
import { compareNames, normalizeName } from '../shared/naming';
import { err, ok, type Result } from '../shared/result';
import type { EmployeeId, JobRoleId, ProjectId, TeamId } from '../shared/ids';

/**
 * Função e equipe.
 *
 * A equipe é como a obra fala — "a Alvenaria 01 sobe o 3º pavimento" —, mas as horas
 * continuam sendo de cada pessoa (RN-053). É por isso que o vínculo é histórico e não um
 * campo `teamId` no funcionário: trocar de equipe no meio do mês não pode reescrever quem
 * trabalhou onde na semana passada.
 */

export const JOB_ROLE_ERROR = {
  NAME_REQUIRED: 'WORKFORCE.JOB_ROLE_NAME_REQUIRED',
  NAME_TAKEN: 'WORKFORCE.JOB_ROLE_NAME_TAKEN',
  NOT_FOUND: 'WORKFORCE.JOB_ROLE_NOT_FOUND',
} as const;

/** Pedreiro, Servente, Eletricista… `Role` já é papel de acesso, daí o nome (RN-013). */
export interface JobRole {
  readonly id: JobRoleId;
  readonly name: string;
  readonly code?: string;
  readonly isActive: boolean;
  readonly deletedAt?: string;
}

export function createJobRole(input: {
  id: JobRoleId;
  name: string;
  code?: string;
}): Result<JobRole, DomainError> {
  const name = input.name.trim();
  if (name.length === 0) {
    return err(domainError(JOB_ROLE_ERROR.NAME_REQUIRED, 'O nome da função é obrigatório.'));
  }
  return ok({
    id: input.id,
    name,
    isActive: true,
    ...(input.code === undefined ? {} : { code: input.code }),
  });
}

/** Nome único, sem diferenciar maiúsculas (RN-013). */
export function jobRoleNaturalKey(name: string): string {
  return normalizeName(name);
}

export function compareJobRoles(a: JobRole, b: JobRole): number {
  return compareNames(a.name, b.name);
}

/**
 * As funções semeadas, com as palavras da folha de pagamento da construtora.
 *
 * A lista nasceu genérica — pedreiro, servente, carpinteiro, armador — e a folha mostrou
 * que **nenhuma delas é usada**: quem assenta bloco é `Oficial de Alvenaria`, quem serve é
 * `Auxiliar de Obra`, quem monta fôrma é `Oficial de Carpintaria`, e o encarregado assina
 * como `Supervisor`. Semear o vocabulário errado não é neutro: o escritório cadastraria a
 * equipe de verdade ao lado, e a obra ficaria com duas listas de funções dizendo o mesmo.
 *
 * ## O nível fica fora do nome
 *
 * A folha escreve `Oficial Carpintaria 4` e `Auxiliar de Obra 1` — vinte e duas variações
 * que são estas famílias com nível de 1 a 5. O nível é **faixa salarial**, e o salário já é
 * de cada pessoa (RN-055): pô-lo no nome da função criaria cadastro novo a cada promoção e
 * uma lista longa para escolher no tablet, sem dizer nada que o salário não diga.
 *
 * ## Três que a folha não tem
 *
 * `Eletricista`, `Encanador` e `Pintor` ficam: a obra tem as disciplinas e as fichas de
 * verificação deles (as FVS 22, 23, 24, 24A, 25A e 25B), e hoje quem as executa é
 * empreiteiro, que não aparece na folha. Sem essas funções, a alocação automática não teria
 * a quem propor a elétrica — e a ausência seria silenciosa.
 *
 * A ordem é a do canteiro: a base primeiro, o oficial depois, e quem supervisiona por
 * último.
 */
export const DEFAULT_JOB_ROLES: readonly string[] = [
  'Auxiliar de Obra',
  'Auxiliar de Almoxarifado',
  'Assistente de Obra',
  'Meio Oficial',
  'Oficial de Alvenaria',
  'Oficial de Carpintaria',
  'Oficial de Armação',
  'Oficial de Construção',
  'Operador de Betoneira',
  'Operador de Guincho',
  'Supervisor',
  'Eletricista',
  'Encanador',
  'Pintor',
];

// ── Equipe ──────────────────────────────────────────────────────────────────────

export const TEAM_ERROR = {
  NAME_REQUIRED: 'WORKFORCE.TEAM_NAME_REQUIRED',
  NAME_TAKEN: 'WORKFORCE.TEAM_NAME_TAKEN',
  NOT_FOUND: 'WORKFORCE.TEAM_NOT_FOUND',
  ALREADY_MEMBER: 'WORKFORCE.ALREADY_MEMBER',
} as const;

export interface Team {
  readonly id: TeamId;
  readonly projectId: ProjectId;
  readonly name: string;
  readonly leaderEmployeeId?: EmployeeId;
  readonly isActive: boolean;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly deletedAt?: string;
}

/**
 * Vínculo do funcionário com a equipe, no tempo.
 *
 * `leftAt` ausente = vínculo aberto, e só pode haver um por funcionário (RN-053). O
 * índice único parcial garante isso no banco, com o mesmo mecanismo do soft delete
 * (ADR-105): quem sai perde a chave e libera o lugar.
 */
export interface TeamMember {
  readonly id: string;
  readonly teamId: TeamId;
  readonly employeeId: EmployeeId;
  readonly projectId: ProjectId;
  readonly joinedAt: string;
  readonly leftAt?: string;
}

export function createTeam(input: {
  id: TeamId;
  projectId: ProjectId;
  name: string;
  leaderEmployeeId?: EmployeeId;
  now: string;
}): Result<Team, DomainError> {
  const name = input.name.trim();
  if (name.length === 0) {
    return err(domainError(TEAM_ERROR.NAME_REQUIRED, 'O nome da equipe é obrigatório.'));
  }
  return ok({
    id: input.id,
    projectId: input.projectId,
    name,
    isActive: true,
    createdAt: input.now,
    updatedAt: input.now,
    ...(input.leaderEmployeeId === undefined ? {} : { leaderEmployeeId: input.leaderEmployeeId }),
  });
}

export function teamNaturalKey(projectId: ProjectId, name: string): string {
  return `${projectId}|${normalizeName(name)}`;
}

/** Chave do vínculo **aberto**: um por funcionário (RN-053). */
export function activeMembershipKey(employeeId: EmployeeId): string {
  return employeeId;
}

export function compareTeams(a: Team, b: Team): number {
  return compareNames(a.name, b.name);
}

export function isOpen(member: TeamMember): boolean {
  return member.leftAt === undefined;
}

/**
 * Fecha o vínculo.
 *
 * Preserva `joinedAt`: o histórico responde "quem estava nesta equipe em março", que é a
 * pergunta que aparece quando alguém confere as horas do mês passado.
 */
export function closeMembership(member: TeamMember, date: string): TeamMember {
  return { ...member, leftAt: date };
}

/**
 * O vínculo valia naquele dia? (RN-053, Fase 30)
 *
 * Entrou até o dia e não tinha saído antes dele. O dia da saída já não conta: quem troca de
 * equipe na quarta fecha a de antes **e** abre a nova com a mesma data (`MoveEmployeeToTeam`),
 * e a quarta é da nova. A comparação é pela data de negócio — o vínculo gravado com hora
 * (`2026-10-14T15:00:00.000Z`) vale o dia inteiro em que nasceu.
 */
export function membershipOpenOn(member: TeamMember, date: string): boolean {
  if (member.joinedAt.slice(0, 10) > date) return false;
  return member.leftAt === undefined || member.leftAt.slice(0, 10) > date;
}

/**
 * A equipe de cada funcionário **naquele dia**, pelo vínculo aberto na data (RN-053).
 *
 * É a equipe do dia, e não a de hoje: conferir a semana passada com a composição desta semana
 * mediria por uma equipe em que a pessoa ainda não estava. Só entram as equipes de `liveTeams`
 * — a excluída deixa de medir, e quem estava nela volta a medir como pessoa.
 */
export function teamsOfEmployeesOn(
  members: readonly TeamMember[],
  date: string,
  liveTeams: ReadonlySet<string>,
): ReadonlyMap<EmployeeId, TeamId> {
  const porFuncionario = new Map<EmployeeId, TeamId>();
  for (const member of members) {
    if (!liveTeams.has(member.teamId) || !membershipOpenOn(member, date)) continue;
    porFuncionario.set(member.employeeId, member.teamId);
  }
  return porFuncionario;
}

/** Composição da equipe para a tela, com a função de cada um já resolvida. */
export interface TeamComposition {
  readonly team: Team;
  readonly members: readonly {
    readonly employeeId: EmployeeId;
    readonly name: string;
    readonly jobRoleId: JobRoleId;
    readonly jobRoleName: string;
    readonly joinedAt: string;
  }[];
}

/**
 * A equipe dentro de uma frase: "a Equipe Estrutura", e não "a equipe Equipe Estrutura" — as
 * equipes costumam se chamar "Equipe …". Sem a palavra no nome, ela entra: "a equipe Pedreiros".
 */
export function equipeNoTexto(teamName: string): string {
  const nome = teamName.trim();
  return /^equipe\b/i.test(nome) ? nome : `equipe ${nome}`;
}
